import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';

const Products = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: productsData, isLoading } = useQuery(
    ['admin-products', searchQuery, statusFilter],
    () => api.get('/admin/products', { 
      params: { search: searchQuery, status: statusFilter } 
    }).then(res => res.data.data),
    { keepPreviousData: true }
  );

  const toggleStatusMutation = useMutation(
    (productId) => api.put(`/admin/products/${productId}/toggle-status`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-products');
        toast.success('تم تحديث حالة المنتج');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const deleteMutation = useMutation(
    (productId) => api.delete(`/admin/products/${productId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-products');
        toast.success('تم حذف المنتج');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">إدارة المنتجات</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث عن منتج..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">جميع المنتجات</option>
              <option value="active">نشط</option>
              <option value="inactive">معطل</option>
            </select>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {productsData?.data?.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="relative h-48 bg-gray-200">
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <ShoppingBagIcon className="w-16 h-16 text-gray-400" />
                  </div>
                )}
                {!product.is_active && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="text-white font-bold">معطل</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                <div className="text-sm text-gray-600 mb-2">
                  البائع: {product.vendor?.store_name}
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  الفئة: {product.category?.name}
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-bold text-primary-600">
                    {product.price} جنيه
                  </span>
                  <span className="text-sm text-gray-500">
                    المخزون: {product.stock}
                  </span>
                </div>
                <div className="flex space-x-2 space-x-reverse">
                  <button
                    onClick={() => toggleStatusMutation.mutate(product.id)}
                    className={`flex-1 px-3 py-2 text-sm rounded-md ${
                      product.is_active
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {product.is_active ? 'تعطيل' : 'تفعيل'}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
                        deleteMutation.mutate(product.id);
                      }
                    }}
                    className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {productsData?.data?.length === 0 && (
          <div className="text-center py-12">
            <ShoppingBagIcon className="mx-auto w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-600">لا توجد منتجات</p>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

export default Products;
